// src/pages/main-streaming-dashboard/components/HostVideoStream.tsx
import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Button } from '../../../components/AntComponents';
import { mediamtxService } from '@/mediaservices/mediamtxService';

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === 'true';

type ConnStrength = 'excellent' | 'good' | 'fair' | 'poor';

interface Stream {
  id: string;
  participantName: string;
  deviceName: string; //device_serialNumber
  device_name?: string; // device_name 
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'camera' | string;
  isHost: boolean;
  isAudioOn: boolean;
  isVideoOn: boolean;
  connectionStrength: ConnStrength;
  /** MediaMTX: WHEP URL for playback */
  whepUrl?: string | null;
  /** MediaSoup: Direct MediaStream for playback */
  mediaStream?: MediaStream | null;
}

interface HostVideoStreamProps {
  stream?: Stream;
  isHost?: boolean;
  onMuteToggle?: (streamId: string) => void;
  onVideoToggle?: (streamId: number) => void;
  onRoleChange?: (streamId: number) => void;
  className?: string;
}

const HostVideoStream: React.FC<HostVideoStreamProps> = ({
  stream,
  isHost = false,
  onMuteToggle,
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [connectionStrength, setConnectionStrength] = useState<ConnStrength>('excellent');
  const [isConnecting, setIsConnecting] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');

  /** Local speaker toggle (only affects this tile). Start muted for autoplay. */
  const [localMuted, setLocalMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // ✅ FIXED: Initialize mute states from current stream status on mount
  const [remoteMicOff, setRemoteMicOff] = useState<boolean>(false);
  const [deviceSelfMuted, setDeviceSelfMuted] = useState<boolean>(false);

  // ✅ FIXED: Initialize video pause states from current stream status on mount
  const [remoteVideoOff, setRemoteVideoOff] = useState<boolean>(false);
  const [deviceSelfVideoPaused, setDeviceSelfVideoPaused] = useState<boolean>(false);

  const [cardKey, setCardKey] = useState(0);

  // ✅ NEW: Sync with current WebSocket state on mount and stream changes
  useEffect(() => {
    if (!USE_MEDIAMTX || !stream) {
      // Reset states when no stream
      setRemoteMicOff(false);
      setDeviceSelfMuted(false);
      setRemoteVideoOff(false);
      setDeviceSelfVideoPaused(false);
      return;
    }

    const deviceKey = String(stream.deviceName || stream.id);

    // Get current state from the active streams in mediamtxService
    const streamSummaries = mediamtxService.getStreamSummaries();
    const currentStream = streamSummaries.find(s => s.id === deviceKey);

    if (currentStream?.meta) {
      const { muteState, videoPauseState } = currentStream.meta;

      // ✅ Apply the current WebSocket state
      setRemoteMicOff(muteState?.adminMuted ?? false);
      setDeviceSelfMuted(muteState?.selfMuted ?? false);
      setRemoteVideoOff(videoPauseState?.adminPaused ?? false);
      setDeviceSelfVideoPaused(videoPauseState?.selfPaused ?? false);
    } else {
      console.log(`ℹ️ HostVideoStream: No cached state found for ${deviceKey}, using defaults`);
      // If no cached state, reset to defaults
      setRemoteMicOff(false);
      setDeviceSelfMuted(false);
      setRemoteVideoOff(false);
      setDeviceSelfVideoPaused(false);
    }
  }, [stream?.deviceName, stream?.id]); // Re-run when device changes

  // ✅ LISTEN: For real-time mute updates from stream_status_update
  useEffect(() => {
    if (!USE_MEDIAMTX || !stream) return;

    const onMuteUpdate = (data: { id: string; muteState: any }) => {
      const deviceKey = String(stream.deviceName || stream.id);
      if (data.id === deviceKey) {
        
        // ✅ Apply the WebSocket state directly
        setRemoteMicOff(data.muteState?.adminMuted ?? false);
        setDeviceSelfMuted(data.muteState?.selfMuted ?? false);
      }
    };

    mediamtxService.on('mute', onMuteUpdate);
    return () => mediamtxService.off('mute', onMuteUpdate);
  }, [stream?.deviceName, stream?.id]);

  // ✅ LISTEN: For real-time video pause updates from stream_status_update
  useEffect(() => {
    if (!USE_MEDIAMTX || !stream) return;

    const onVideoPauseStateUpdate = (data: { id: string; videoPauseState: any }) => {
      const deviceKey = String(stream.deviceName || stream.id);
      if (data.id === deviceKey) {
        // ✅ Apply the WebSocket state directly
        setRemoteVideoOff(data.videoPauseState?.adminPaused ?? false);
        setDeviceSelfVideoPaused(data.videoPauseState?.selfPaused ?? false);
      }
    };

    mediamtxService.on('videoPauseState', onVideoPauseStateUpdate);
    return () => mediamtxService.off('videoPauseState', onVideoPauseStateUpdate);
  }, [stream?.deviceName, stream?.id]);

  // ✅ LISTEN: For streamStatusUpdate to sync with the complete WebSocket state
  useEffect(() => {
    if (!USE_MEDIAMTX || !stream) return;

    const onStreamStatusUpdate = (data: any) => {
      const deviceKey = String(stream.deviceName || stream.id);
      
      // Check if this update includes our device
      const muteState = data?.muteStates?.[deviceKey];
      const videoPauseState = data?.videoPauseStates?.[deviceKey];
      
      if (muteState !== undefined) {
        setRemoteMicOff(muteState?.adminMuted ?? false);
        setDeviceSelfMuted(muteState?.selfMuted ?? false);
      }
      
      if (videoPauseState !== undefined) {
        setRemoteVideoOff(videoPauseState?.adminPaused ?? false);
        setDeviceSelfVideoPaused(videoPauseState?.selfPaused ?? false);
      }
    };

    mediamtxService.on('streamStatusUpdate', onStreamStatusUpdate);
    return () => mediamtxService.off('streamStatusUpdate', onStreamStatusUpdate);
  }, [stream?.deviceName, stream?.id]);

  // ✅ GET MIC ICON: Show correct visual state based on WebSocket truth
  const getMicIconAndStyle = () => {
    if (remoteMicOff) {
      // adminMuted: true - Operator muted this device
      return {
        icon: 'MicOff' as const,
        className: 'text-red-500', // Red = admin muted
        title: 'Audio muted by operator'
      };
    } else if (deviceSelfMuted) {
      // selfMuted: true - Device muted themselves
      return {
        icon: 'MicOff' as const,
        className: 'text-orange-400', // Orange = self muted
        title: 'Audio muted by device'
      };
    } else {
      // Both false - Audio is active
      return {
        icon: 'Mic' as const,
        className: 'text-white',
        title: 'Audio active - Click to mute'
      };
    }
  };

  // ✅ GET VIDEO ICON: Show correct visual state based on WebSocket truth
  const getVideoIconAndStyle = () => {
    if (remoteVideoOff) {
      // adminPaused: true - Operator paused this video
      return {
        icon: 'VideoOff' as const,
        className: 'text-red-500', // Red = admin paused
        title: 'Video paused by operator'
      };
    } else if (deviceSelfVideoPaused) {
      // selfPaused: true - Device paused their own video
      return {
        icon: 'VideoOff' as const,
        className: 'text-orange-400', // Orange = self paused
        title: 'Video paused by device'
      };
    } else {
      // Both false - Video is active
      return {
        icon: 'Video' as const,
        className: 'text-white',
        title: 'Video active - Click to pause'
      };
    }
  };

  const toggleRemoteMic = () => {
    if (!stream?.deviceName) return;

    const deviceKey = String(stream.deviceName);
    const nextAdminMuted = !remoteMicOff; // Toggle admin mute state
    
    if (USE_MEDIAMTX) {
      mediamtxService.setMicMuted(deviceKey, nextAdminMuted);
    } else {
      onMuteToggle?.(stream.id);
    }
  };

  const toggleRemoteVideo = () => {
    if (!stream?.deviceName) return;

    const deviceKey = String(stream.deviceName);
    const nextAdminPaused = !remoteVideoOff; // Toggle admin pause state
    
    
    if (USE_MEDIAMTX) {
      mediamtxService.setVideoPaused(deviceKey, nextAdminPaused);
    }
  };

  // ---------------------------
  // WHEP playback (MediaMTX) with reconnection logic
  // ---------------------------
  useEffect(() => {
    if (!USE_MEDIAMTX) return; // Skip if using MediaSoup
    
    const v = videoRef.current;
    const url = stream?.whepUrl || null;
    if (!v || !url) {
      setIsConnecting(false);
      return;
    }

    let pc: RTCPeerConnection | null = null;
    let aborted = false;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    // Set connecting state when starting WHEP connection
    setIsConnecting(true);

    if (v.srcObject) {
      (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }

    v.autoplay = true;
    v.playsInline = true;
    v.muted = localMuted; // autoplay-friendly

    const handleConnectionStateChange = (peer: RTCPeerConnection) => {
      const state = peer.connectionState;
      setConnectionState(state);
      
      if (state === 'connected') {
        setIsConnecting(false);
        console.log(`[HostVideoStream ${stream?.deviceName}] Connection established`);
      }

      // If connection fails/disconnects, trigger a reconnect after 5 seconds
      if (state === 'failed' || state === 'disconnected') {
        console.log(`[HostVideoStream ${stream?.deviceName}] Connection lost, will attempt reconnect in 5 seconds`);
        setIsConnecting(true);
        
        // Clear any existing timeout
        if (reconnectTimeout) clearTimeout(reconnectTimeout);
        
        reconnectTimeout = setTimeout(() => {
          if (!aborted && stream?.whepUrl) {
            console.log(`[HostVideoStream ${stream?.deviceName}] Triggering reconnect`);
            setReconnectTrigger(prev => prev + 1);
          }
        }, 5000); // Wait 5 seconds before reconnecting
      }
    };

    const startWhepConnection = async () => {
      try {
        pc = new RTCPeerConnection();

        // Monitor connection state changes
        pc.onconnectionstatechange = () => handleConnectionStateChange(pc!);

        pc.addTransceiver('video', { direction: 'recvonly' });
        pc.addTransceiver('audio', { direction: 'recvonly' });

        pc.ontrack = (e) => {
          if (aborted) return;
          if (v && v.srcObject !== e.streams[0]) {
            v.srcObject = e.streams[0];
            setConnectionState('connected');
            setIsConnecting(false);
            console.log(`[HostVideoStream ${stream?.deviceName}] Stream track received`);
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: offer.sdp || '',
        });
        
        if (!res.ok) throw new Error(`WHEP POST failed: ${res.status}`);
        
        const answerSdp = await res.text();
        if (aborted) return;
        
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        try {
          await v.play();
        } catch (err) {
          // will usually succeed while muted; user can unmute via the tile button
          console.warn('HostVideoStream autoplay blocked:', err);
        }
      } catch (err) {
        console.error(`[HostVideoStream ${stream?.deviceName}] WHEP init error:`, err);
        setConnectionState('failed');
        setIsConnecting(false);
      }
    };

    startWhepConnection();

    return () => {
      aborted = true;
      setIsConnecting(false);
      
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      
      try {
        if (pc) {
          pc.getSenders().forEach((s) => s.track && s.track.stop());
          pc.getReceivers().forEach((r) => r.track && r.track.stop && r.track.stop());
          pc.close();
        }
      } catch {}
      pc = null;

      if (v && v.srcObject) {
        (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        v.srcObject = null;
      }
    };
  }, [stream?.whepUrl, localMuted, reconnectTrigger]);

  // ---------------------------
  // MediaSoup playback (Direct MediaStream)
  // ---------------------------
  useEffect(() => {
    if (USE_MEDIAMTX) return; // Skip if using MediaMTX
    
    const v = videoRef.current;
    const mediaStream = stream?.mediaStream || null;
    if (!v || !mediaStream) return;

    v.autoplay = true;
    v.playsInline = true;
    v.muted = localMuted;
    v.srcObject = mediaStream;
    
    // Attempt to play
    v.play().catch((err) => {
      console.warn('HostVideoStream MediaSoup autoplay failed:', err);
    });

    return () => {
      // Don't stop tracks - MediaSoup service manages the lifecycle
      if (v && v.srcObject === mediaStream) {
        v.srcObject = null;
      }
    };
  }, [stream?.mediaStream, localMuted]);

  // Apply local-muted to the element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = localMuted;
    if (!localMuted) {
      v.play().catch(() => {});
    }
  }, [localMuted]);

  // Cosmetic: simulate connection strength (you can wire a real value if you have one)
  useEffect(() => {
    const interval = setInterval(() => {
      const strengths: ConnStrength[] = ['excellent', 'good', 'fair', 'poor'];
      setConnectionStrength(strengths[Math.floor(Math.random() * strengths.length)]);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const getDeviceIcon = (deviceType?: Stream['deviceType']) => {
    switch (deviceType) {
      case 'desktop': return 'Monitor';
      case 'mobile': return 'Smartphone';
      case 'tablet': return 'Tablet';
      case 'camera': return 'Camera';
      default: return 'Video';
    }
  };

  const id = stream?.id;
  
  // Determine if we have a playable media source
  const hasMediaSource = USE_MEDIAMTX ? !!stream?.whepUrl : !!stream?.mediaStream;

  const micIconData = getMicIconAndStyle();
  const videoIconData = getVideoIconAndStyle();

  useEffect(() => {
  if (!USE_MEDIAMTX || !stream) return;

  const deviceKey = String(stream.deviceName || stream.id);

  const onDeviceReconnected = (data: {
    deviceSerialNumber: string;
    reconnected: boolean;
    isHost: boolean;
    isCohost: boolean;
    timestamp: number;
  }) => {
    if (data.deviceSerialNumber === deviceKey && data.reconnected) {
      console.log(`🔄 Device ${deviceKey} reconnected, refreshing page in 2 seconds...`);
      
      // 2-second delay before page refresh
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  mediamtxService.on('device_reconnecting', onDeviceReconnected);
  return () => mediamtxService.off('device_reconnecting', onDeviceReconnected);
}, [stream?.deviceName, stream?.id]);

  return (
    <div
      key={cardKey}
      className={`relative bg-black rounded-lg overflow-hidden aspect-video ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* --- Video Layer --- */}
      <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        {hasMediaSource ? (
          <>
            <video ref={videoRef} className="w-full h-full object-contain" />
            
            {/* Connecting/Reconnecting Overlay */}
            {isConnecting && (
              <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                  <Icon name="Loader2" size={48} className="text-white animate-spin" />
                </div>
                <span className="text-lg font-medium text-white">
                  {connectionState === 'new' || connectionState === 'connecting' ? 'Connecting...' : 'Reconnecting...'}
                </span>
                <span className="text-sm text-slate-300">
                  {connectionState === 'new' || connectionState === 'connecting' 
                    ? 'Establishing video stream connection' 
                    : 'Attempting to restore connection'}
                </span>
              </div>
            )}
            
            {/* ✅ UPDATED: Video Paused Overlay - shows different messages based on who paused */}
            {(remoteVideoOff || deviceSelfVideoPaused || !stream?.isVideoOn) && !isConnecting && (
              <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                  <Icon name="Pause" size={48} className="text-white" />
                </div>
                <span className="text-lg font-medium text-white">
                  {remoteVideoOff ? 'Video Paused by Operator' : 
                   deviceSelfVideoPaused ? 'Video Paused by Device' : 'Video Paused'}
                </span>
                <span className="text-sm text-slate-300">
                  {remoteVideoOff ? 'Stream video has been paused by the operator' : 
                   deviceSelfVideoPaused ? 'Device has paused their video stream' : 
                   'Stream video is currently paused'}
                </span>
              </div>
            )}
          </>
        ) : stream?.isVideoOn ? (
          <div className="w-full h-full bg-slate-700 flex items-center justify-center">
            <Icon name="User" size={48} className="text-slate-400" />
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <Icon name="VideoOff" size={32} className="text-slate-400" />
            <span className="text-sm text-slate-400">Video Off</span>
          </div>
        )}
      </div>

      {/* --- Device Info Overlay (top-left) --- */}
      <div className="absolute top-3 left-3 flex items-center space-x-2 bg-black bg-opacity-60 px-2 py-1 rounded">
        <Icon name={getDeviceIcon(stream?.deviceType)} size={14} className="text-white" />
        <span className="text-xs text-white font-medium">{stream?.device_name}</span>
      </div>

      {/* ✅ UPDATED: Audio muted badge - shows different colors for admin vs self mute */}
      {(deviceSelfMuted || remoteMicOff || !stream?.isAudioOn) && (
        <div className={`absolute bottom-3 left-3 p-1 rounded ${
          remoteMicOff ? 'bg-red-600' : deviceSelfMuted ? 'bg-orange-500' : 'bg-error'
        } bg-opacity-80`}>
          <Icon name="MicOff" size={14} className="text-white" />
        </div>
      )}

      {/* --- Hover Controls Overlay --- */}
      {isHovered && !isConnecting && (
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="flex items-center space-x-3">
      
            {/* ✅ UPDATED: Remote MIC toggle with correct icon colors */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleRemoteMic}
              className="bg-black bg-opacity-60 hover:bg-black hover:bg-opacity-80"
              title={micIconData.title}
            >
              <Icon 
                name={micIconData.icon} 
                size={16} 
                className={micIconData.className}
              />
            </Button>

            {/* ✅ UPDATED: Video toggle button with correct icon colors */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleRemoteVideo}
              className="bg-black bg-opacity-60 hover:bg-black hover:bg-opacity-80"
              title={videoIconData.title}
            >
              <Icon 
                name={videoIconData.icon} 
                size={16} 
                className={videoIconData.className}
              />
            </Button>

            {/* Local speaker (only affects this tile) */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocalMuted((m) => !m)}
              className="bg-black bg-opacity-60 text-white hover:bg-black hover:bg-opacity-80"
              title={localMuted ? 'Enable Sound (local)' : 'Mute Sound (local)'}
            >
              <Icon name={localMuted ? 'VolumeX' : 'Volume2'} size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostVideoStream;
